import { all, select, takeEvery, fork, call, put, delay } from 'redux-saga/effects'

// action
import { adminUsersActions, configActions, routeActions } from '@redux/store/index'
import { AdminUsersStateType } from '@redux/index'

// api
import { usersApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// type
import { User, UserType } from '@schemas'

const apiGetUsers = async (
	includingDeletedUsersYn: string,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await usersApi.getUsers({
		query: {
			includingDeletedUsersYn: includingDeletedUsersYn,
		},
	})
	return res
}

const apiBatchUpdateUsers = async (
	body: {
		iudFlag: string
		id: string
		name: string
		email: string
		language: string
		useYn: number
	}[],
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await usersApi.batchUpdateUsers({
		body: { dataList: body },
	})
	return res
}

const apiUpdateUserPassword = async ({
	id,
	name,
	password,
}: {
	id: string
	name: string
	password: string
}) => {
	const res: ResType | undefined = await usersApi.updateUserPassword({
		params: { id: id },
		body: {
			name: name,
			password: password,
		},
	})
	return res
}

const apiUpdateUserProfileImage = async (userId: string, files: File[]) => {
	const res: ResType | undefined = await usersApi.updateUserProfileImage({
		params: {
			id: userId,
		},
		files: files,
	})
	return res
}

function* triggerInitAdminUsers() {
	try {
		yield put(adminUsersActions.USER_LIST_STATUS('loading'))
		const apiRes: ResType = yield call(apiGetUsers, '0')

		if (apiRes.status === 200) {
			const tempUserList =
				apiRes.data[0].users !== undefined ? (apiRes.data[0].users as any[]) : []
			const newUserlist = tempUserList.map(aUser => new User(aUser as UserType))
			yield put(adminUsersActions.USER_LIST(newUserlist))

			if (apiRes.data.length > 0) {
				yield put(adminUsersActions.USER_LIST_STATUS('ok'))
			} else {
				yield put(adminUsersActions.USER_LIST_STATUS('empty'))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateResetPassword() {
	try {
		const updateInfo: AdminUsersStateType['updateResetPassword'] = yield select(
			state => state.adminUsers.updateResetPassword,
		)

		const apiRes: ResType = yield call(apiUpdateUserPassword, updateInfo)

		if (apiRes !== undefined && apiRes.status === 201) {
			yield put(
				configActions.TOAST({ isShow: true, message: '패스워드가 업데이트 되었습니다.' }),
			)
			delay(2000)
			yield put(routeActions.NAVIGATE_PATH('/admin/users'))
		} else {
			yield put(
				adminUsersActions.RESET_PASSWORD_ERROR_MESSAGE(
					'패스워드 업데이트에 실패하였습니다.',
				),
			)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* editUserProfileImage() {
	try {
		yield put(routeActions.NAVIGATE_PATH('/admin/user-profile-image-update'))
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateProfileImage() {
	try {
		const profileImageInfo: AdminUsersStateType['upldateProfileImage'] = yield select(
			state => state.adminUsers.upldateProfileImage,
		)

		// 이 화면은 그리드에서 고른 사용자를 들고 들어온다. 새로고침하면 그 선택이 사라지므로
		// 빈 ID 로 서버를 부르지 않는다 — 부르면 404 가 나고 화면엔 아무 말도 안 나왔다.
		if (profileImageInfo.userId === '') {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: '대상 사용자가 없습니다. 목록에서 다시 선택해 주세요.',
				}),
			)
			yield put(routeActions.NAVIGATE_PATH('/admin/users'))
			return
		}

		const apiRes: ResType = yield call(
			apiUpdateUserProfileImage,
			profileImageInfo.userId,
			[profileImageInfo.profileImage] as File[],
		)

		// 예전에는 응답을 받아 놓고 아무것도 하지 않았다. 성공해도 화면이 그대로여서
		// "저장을 눌러도 아무 일도 없다" 로 보였다.
		if (apiRes !== undefined && [200, 201].includes(apiRes.status)) {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: '프로필 이미지가 변경되었습니다.',
				}),
			)
			yield put(adminUsersActions.TRIGGER_INIT_ADMIN_USERS(true))
			yield put(routeActions.NAVIGATE_PATH('/admin/users'))
		} else {
			// 상태코드가 사유다 (AVATAR_PLAN §5). 한 문장으로 뭉개면 무엇을 고쳐야 할지 알 수 없다.
			const status: number = apiRes?.status ?? 0
			yield put(
				configActions.TOAST({
					isShow: true,
					message:
						status === 413
							? '파일이 너무 큽니다. 8MB 까지 올릴 수 있습니다.'
							: status === 400
							? '이미지로 읽을 수 없는 파일입니다.'
							: status === 404
							? '대상 사용자를 찾을 수 없습니다.'
							: status === 429
							? '이 계정의 사진을 오늘 너무 여러 번 바꿨습니다. 내일 다시 시도해 주세요.'
							: '이미지를 올리지 못했습니다.',
				}),
			)
		}
	} catch (error) {
		Logger.warn(error)
		yield put(
			configActions.TOAST({ isShow: true, message: '이미지를 올리지 못했습니다.' }),
		)
	}
}

function* updateUser() {
	try {
		const body: AdminUsersStateType['updateUser'] = yield select(
			state => state.adminUsers.updateUser,
		)

		yield call(apiBatchUpdateUsers, [body])
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(adminUsersActions.TRIGGER_INIT_ADMIN_USERS, triggerInitAdminUsers)
	yield takeEvery(adminUsersActions.UPDATE_RESET_PASSWORD, updateResetPassword)
	yield takeEvery(adminUsersActions.EDIT_USER_PROFILE_IMAGE, editUserProfileImage)
	yield takeEvery(adminUsersActions.UPLDATE_PROFILE_IMAGE, updateProfileImage)
	yield takeEvery(adminUsersActions.UPDATE_USER, updateUser)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
